// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving the ownership history of assets.
 * Provides functionality to track all ownership changes and transfers
 * that have occurred for a specific asset over time.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetOwnershipEventSchema } from '@/asset/api/types';
import { z } from 'zod';

/**
 * Schema for validating the API response when fetching asset ownership history.
 * Transforms the response to provide a more intuitive structure with
 * the ownership events array separated from metadata.
 */
const responseSchema = z
  .object({
    data: z.array(assetOwnershipEventSchema),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, events: data }));

/**
 * Retrieves the complete ownership history for a specific asset.
 * This function handles the API call to fetch all ownership events and validates
 * the response data using Zod schemas. The history includes all transfers
 * and ownership changes from the asset's creation to present.
 *
 * @param key - Unique identifier of the asset to fetch ownership history for
 * @returns Promise resolving to the list of ownership events and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getAssetOwnership(key: string) {
  const path = `/v1/asset/${key}/ownership`;
  const response = await api.get(path);

  return parseResponse(responseSchema, response.data, {
    schemaName: 'AssetOwnership',
    endpoint: path,
  });
}
